import { useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import StyleTwoToneIcon from "@mui/icons-material/StyleTwoTone";
import type { Tag } from "../types/types.ts";
import { PaletteColor, PaletteColorHex } from "../types/PaletteColor.ts";
import { useDarkMode } from "../hooks/useDarkMode.ts";

interface Props {
  tags: Tag[];
  onClose: () => void;
  onAddTag: (name: string, color: string) => Promise<void>;
  onDeleteTag: (tagId: number) => Promise<void>;
  onUpdateTag: (tagId: number, name: string, color: string) => Promise<void>;
  color?: PaletteColor;
}

const PALETTE_COLORS = Object.values(PaletteColorHex);

export function TagManagementDialog({
  tags,
  onClose,
  onAddTag,
  onDeleteTag,
  onUpdateTag,
  color = "Blue",
}: Props) {
  const isDarkMode = useDarkMode();
  const accent = PaletteColorHex[color];
  const [newTagName, setNewTagName] = useState("");
  const [newTagColor, setNewTagColor] = useState(PALETTE_COLORS[0]);

  const [editingTagId, setEditingTagId] = useState<number | null>(null);
  const [editName, setEditName] = useState("");
  const [editColor, setEditColor] = useState("");

  const handleAddClick = () => {
    if (newTagName.trim() && !tags.find((t) => t.name === newTagName.trim())) {
      onAddTag(newTagName.trim(), newTagColor);
      setNewTagName("");
      setNewTagColor(PALETTE_COLORS[0]);
    }
  };

  const handleInputKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleAddClick();
    }
  };

  const startEdit = (tag: Tag) => {
    setEditingTagId(tag.id);
    setEditName(tag.name);
    setEditColor(tag.color);
  };

  const cancelEdit = () => {
    setEditingTagId(null);
    setEditName("");
    setEditColor("");
  };

  const handleSaveEdit = () => {
    if (editingTagId == null || !editName.trim()) return;
    const duplicate = tags.find(
      (t) => t.id !== editingTagId && t.name === editName.trim(),
    );
    if (duplicate) return;
    onUpdateTag(editingTagId, editName.trim(), editColor);
    setEditingTagId(null);
    setEditName("");
    setEditColor("");
  };

  const handleEditKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSaveEdit();
    } else if (e.key === "Escape") {
      cancelEdit();
    }
  };

  const inputClass = `w-full p-2 border rounded focus:outline-none focus:ring-2 ${
    isDarkMode
      ? "bg-gray-800 border-gray-700 text-gray-100"
      : "bg-white border-gray-300 text-gray-800"
  }`;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] p-4"
      onClick={onClose}
    >
      <div
        className={`w-96 max-w-full max-h-[85vh] flex flex-col rounded-xl overflow-hidden shadow-2xl border-2 ${
          isDarkMode ? "border-black" : "border-white"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ヘッダー */}
        <div
          className="flex items-center justify-between px-4 py-3"
          style={{
            background: `linear-gradient(135deg, ${accent}, ${accent}CC)`,
          }}
        >
          <div className="flex items-center gap-2 text-white">
            <StyleTwoToneIcon fontSize="small" />
            <span className="font-bold text-sm">タグを追加・編集</span>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-full text-white hover:bg-white/20"
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>

        {/* ボディ */}
        <div
          className={`flex-1 flex flex-col min-h-0 p-4 ${
            isDarkMode ? "bg-gray-900" : "bg-white"
          }`}
        >
          <div className="space-y-4">
            <div>
              <input
                type="text"
                value={newTagName}
                onChange={(e) => setNewTagName(e.target.value)}
                onKeyPress={handleInputKeyPress}
                className={inputClass}
                style={{ caretColor: accent }}
                placeholder="新しいタグ名"
              />
            </div>
            <div className="grid grid-cols-5 gap-2 p-3 justify-items-center">
              {PALETTE_COLORS.map((c) => (
                <button
                  key={c}
                  style={{
                    backgroundColor: c,
                    boxShadow:
                      newTagColor === c ? `0 0 0 2px ${accent}` : undefined,
                  }}
                  onClick={() => setNewTagColor(c)}
                  className={`w-8 h-8 rounded-full cursor-pointer transition-all transform hover:scale-110 shadow-md ${
                    newTagColor === c
                      ? "ring-2 ring-offset-2"
                      : "ring-2 ring-white"
                  } ${
                    newTagColor === c
                      ? isDarkMode
                        ? "ring-offset-gray-900"
                        : "ring-offset-white"
                      : ""
                  }`}
                  aria-label={`Color ${c}`}
                />
              ))}
            </div>
            <button
              onClick={handleAddClick}
              className="w-full px-4 py-2 text-white rounded hover:opacity-90 transition-opacity disabled:opacity-50"
              style={{ backgroundColor: accent }}
              disabled={!newTagName.trim()}
            >
              追加
            </button>
          </div>

          <div
            className={`flex-grow space-y-2 max-h-60 overflow-y-auto mt-4 border-t pt-4 ${
              isDarkMode ? "border-gray-700" : "border-gray-200"
            }`}
          >
            {tags.length > 0 ? (
              tags.map((tag) =>
                editingTagId === tag.id ? (
                  <div
                    key={tag.id}
                    className={`space-y-2 p-2 rounded ${
                      isDarkMode ? "bg-gray-800" : "bg-gray-50"
                    }`}
                  >
                    <input
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      onKeyDown={handleEditKeyPress}
                      className={`w-full p-1.5 border rounded text-sm focus:outline-none focus:ring-2 ${
                        isDarkMode
                          ? "bg-gray-900 border-gray-700 text-gray-100"
                          : "bg-white border-gray-300 text-gray-800"
                      }`}
                      style={{ caretColor: accent }}
                      autoFocus
                    />
                    <div className="grid grid-cols-5 gap-1.5 justify-items-center">
                      {PALETTE_COLORS.map((c) => (
                        <button
                          key={c}
                          style={{
                            backgroundColor: c,
                            boxShadow:
                              editColor === c ? `0 0 0 2px ${accent}` : undefined,
                          }}
                          onClick={() => setEditColor(c)}
                          className={`w-6 h-6 rounded-full cursor-pointer transition-all transform hover:scale-110 shadow-sm ${
                            editColor === c
                              ? "ring-2 ring-offset-1"
                              : "ring-1 ring-white"
                          } ${
                            editColor === c
                              ? isDarkMode
                                ? "ring-offset-gray-800"
                                : "ring-offset-white"
                              : ""
                          }`}
                          aria-label={`Color ${c}`}
                        />
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={handleSaveEdit}
                        disabled={!editName.trim()}
                        className="flex-1 px-2 py-1 text-white rounded text-xs hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-1"
                        style={{ backgroundColor: accent }}
                      >
                        <CheckIcon fontSize="inherit" />
                        保存
                      </button>
                      <button
                        onClick={cancelEdit}
                        className={`flex-1 px-2 py-1 rounded text-xs flex items-center justify-center gap-1 ${
                          isDarkMode
                            ? "bg-gray-700 text-gray-200 hover:bg-gray-600"
                            : "bg-gray-300 text-gray-700 hover:bg-gray-400"
                        }`}
                      >
                        キャンセル
                      </button>
                    </div>
                  </div>
                ) : (
                  <div
                    key={tag.id}
                    className="flex justify-between items-center p-2 rounded"
                  >
                    <div
                      style={{
                        color: tag.color,
                        backgroundColor: `${tag.color}20`,
                      }}
                      className="px-3 py-1 rounded-full text-xs font-semibold"
                    >
                      #{tag.name}
                    </div>
                    <div className="flex gap-2 items-center">
                      <button
                        onClick={() => startEdit(tag)}
                        className="text-sm rounded-lg px-2 py-0.5 border hover:opacity-80"
                        style={{ color: accent, borderColor: `${accent}80` }}
                      >
                        編集
                      </button>
                      <button
                        onClick={() => onDeleteTag(tag.id)}
                        className="text-sm text-red-600 border border-red-400 rounded-lg px-2 py-0.5 hover:bg-red-50"
                      >
                        削除
                      </button>
                    </div>
                  </div>
                ),
              )
            ) : (
              <div
                className={`text-center pt-4 ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                登録されているタグはありません。
              </div>
            )}
          </div>

          <div
            className={`flex justify-end mt-4 border-t pt-4 ${
              isDarkMode ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <button
              onClick={onClose}
              className="px-4 py-2 text-white rounded hover:opacity-90 transition-opacity"
              style={{ backgroundColor: accent }}
            >
              完了
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
